import React, { useCallback, useEffect, useState } from "react";
import {
  Activity, RefreshCw, CheckCircle2, XCircle, Clock, TrendingUp, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { AdminKpiCard, AdminSkeleton } from "../ui";

/**
 * TrackingUpdaterWidget — Sprint A Finalisatie (22 Feb 2026).
 *
 * Toont de 6 metrics uit `GET /api/admin/tracking-updater/stats`:
 *   - Actieve consolidaties
 *   - Vandaag bijgewerkt
 *   - Updates in verwerking
 *   - Mislukte updates
 *   - Laatste synchronisatie
 *   - Gemiddelde verwerkingstijd
 *
 * Plus refresh-knop die `POST /admin/tracking-updater/refresh` aanroept om
 * historische data te backfillen (idempotent — dedupe in backend). De widget
 * kan hierdoor als "health check" fungeren voor de hele tracking-pipeline.
 */
export default function TrackingUpdaterWidget() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const r = await adminApi.get("/admin/tracking-updater/stats");
      setStats(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await adminApi.post("/admin/tracking-updater/refresh");
      setStats(r.data?.stats || stats);
      const inserted = r.data?.result?.inserted || 0;
      toast.success(
        inserted > 0
          ? `Tracking updater bijgewerkt — ${inserted} nieuwe event${inserted === 1 ? "" : "s"}`
          : "Tracking updater is up-to-date"
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRefreshing(false);
    }
  };

  const lastSyncPretty = stats?.last_sync
    ? new Date(stats.last_sync).toLocaleString("nl-NL", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";

  const avgPretty = stats?.average_processing_time_seconds
    ? `${stats.average_processing_time_seconds.toFixed(1)}s`
    : "—";

  return (
    <div className="space-y-3" data-testid="tracking-updater-widget">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <Activity className="w-4 h-4 text-blue-600" />
            Tracking Updater
          </div>
          <div className="text-xs text-slate-500">
            Automatische verwerking van consolidatie- en shipmentstatussen
          </div>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing || loading}
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-slate-200 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="tracking-updater-refresh-btn"
        >
          {refreshing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}
          Ververs
        </button>
      </div>

      {loading ? (
        <AdminSkeleton variant="kpi-grid" count={6} />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <AdminKpiCard
            label="Actieve consolidaties"
            value={stats?.active_consolidations ?? 0}
            icon={Activity} tone="brand"
            testid="tracking-updater-active-consolidations"
          />
          <AdminKpiCard
            label="Vandaag bijgewerkt"
            value={stats?.updated_today ?? 0}
            icon={TrendingUp} tone="emerald"
            testid="tracking-updater-updated-today"
          />
          <AdminKpiCard
            label="In verwerking"
            value={stats?.in_progress ?? 0}
            icon={Loader2} tone="amber"
            testid="tracking-updater-in-progress"
          />
          <AdminKpiCard
            label="Mislukte updates"
            value={stats?.failed_updates ?? 0}
            icon={XCircle} tone={stats?.failed_updates > 0 ? "red" : "slate"}
            testid="tracking-updater-failed"
          />
          <AdminKpiCard
            label="Laatste sync"
            value={lastSyncPretty}
            icon={Clock} tone="blue"
            testid="tracking-updater-last-sync"
          />
          <AdminKpiCard
            label="Gem. verwerkingstijd"
            value={avgPretty}
            icon={CheckCircle2} tone="slate"
            testid="tracking-updater-avg-time"
          />
        </div>
      )}

      {stats?.breakdown_last_7d && stats.breakdown_last_7d.length > 0 && (
        <div className="mt-2" data-testid="tracking-updater-breakdown">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
            Status-verdeling (7 dagen)
          </div>
          <div className="flex flex-wrap gap-1.5">
            {stats.breakdown_last_7d.slice(0, 8).map((b) => (
              <div
                key={b.status}
                className="px-2 py-0.5 text-[11px] rounded-full bg-slate-100 text-slate-700 border border-slate-200"
              >
                <span className="font-medium">{b.status.replace(/_/g, " ")}</span>
                <span className="ml-1.5 text-slate-500">· {b.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
